import { token } from '@atlaskit/tokens';
import Z_INDEX from '@databases/layout/z-index';
import { getGridTemplateColumns } from '@databases/quick-calc/utils';
import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import type React from 'react';

import {
  getCellsFromRow,
  getRowFromCell,
  getRowsFromBody,
  getTableFromBodyRow
} from '../../../table';

const SELECT_BUTTON_HEIGHT = 14;

interface BuildFieldDragImageFromStateArgs {
  e: React.DragEvent;
  selectedFieldIds: string[];
  thRef: React.RefObject<HTMLTableCellElement>;
  tableBodyRef: React.RefObject<HTMLTableSectionElement | null>;
  dragImageRef: React.MutableRefObject<HTMLElement | undefined>;
}

/**
 * Builds the drag image from selections state by finding selected columns
 * via data-field-id. Mirrors the legacy visual structure but uses attribute
 * selectors instead of IS_SELECTED CSS class.
 */
export const buildFieldDragImageFromState = ({
  e,
  selectedFieldIds,
  thRef,
  tableBodyRef,
  dragImageRef
}: BuildFieldDragImageFromStateArgs) => {
  const tr = getRowFromCell(thRef.current);
  const table = getTableFromBodyRow(tr);
  const tbody = tableBodyRef.current;
  const ths = getCellsFromRow(tr);

  // Find selected <th> elements by data-field-id
  const selectedThs = selectedFieldIds
    .map((id) => ths.find((th) => th.getAttribute('data-field-id') === id))
    .filter((el): el is HTMLTableCellElement => el != null);

  const currentIndexInSelectedThs = selectedThs.indexOf(thRef.current!);
  const selectedThsIndexOffsetWidth = selectedThs
    .slice(0, currentIndexInSelectedThs + 1)
    .reduce((p, c) => p + c.clientWidth, 0);
  const selectedThsWidths = selectedThs.map((el) => el.clientWidth);
  const selectedThsWidth = selectedThsWidths.reduce((p, c) => p + c, 0);

  dragImageRef.current = document.createElement('div');
  const newThead = document.createElement('thead');
  const newTBody = document.createElement('tbody');
  const newTr = document.createElement('tr');

  // Table header copy — clone the header row and filter to selected columns
  const trClone = tr?.cloneNode(true) as HTMLTableRowElement | null;
  const clonedThs = getCellsFromRow(trClone);
  const clonedSelectedThs = selectedFieldIds
    .map((id) => clonedThs.find((th) => th.getAttribute('data-field-id') === id))
    .filter((el): el is HTMLTableCellElement => el != null);

  //Hide Select handles
  clonedSelectedThs.forEach((th) => {
    const button = th.querySelector(
      `.${TABLE_CLASSNAMES.SELECT_BUTTON}`
    ) as HTMLElement | null;
    if (!button) {
      return;
    }

    button.style.display = 'none';
  });

  // Table body copy — clone tbody and filter each row to selected columns
  const tbodyClone = tbody?.cloneNode(true) as HTMLTableSectionElement | null;
  const clonedTrs = getRowsFromBody(tbodyClone);
  clonedTrs?.forEach((el) => {
    const trCopy = newTr.cloneNode(false) as HTMLTableRowElement;
    const clonedCells = getCellsFromRow(el);
    const selectedClonedCells = selectedFieldIds
      .map((id) => clonedCells.find((td) => td.getAttribute('data-field-id') === id))
      .filter((el): el is HTMLTableCellElement => el != null);
    // eslint-disable-next-line canvas/no-spread-arguments
    trCopy.append(...selectedClonedCells);
    newTBody.append(trCopy);
    Object.assign(trCopy.style, {
      display: 'grid',
      gridTemplateColumns: getGridTemplateColumns(selectedThsWidths)
    });
  });

  // Clone the html table element to get the styles (but do not include descendants)
  const tableClone = table?.cloneNode(false) as HTMLTableElement | null;
  if (tableClone) {
    Object.assign(dragImageRef.current.style, {
      position: 'absolute',
      top: 0,
      left: 0,
      zIndex: Z_INDEX.DRAG_IMAGE_ELEMENT,
      overflow: 'hidden'
    });
    Object.assign(tableClone.style, {
      minWidth: `${selectedThsWidth}px`
    });
    Object.assign(newTr.style, {
      display: 'grid',
      gridTemplateColumns: getGridTemplateColumns(selectedThsWidths)
    });

    tableClone.append(newThead);
    tableClone.append(newTBody);
    // eslint-disable-next-line canvas/no-spread-arguments
    newTr.append(...clonedSelectedThs);
    newThead.append(newTr);
    dragImageRef.current.append(tableClone);

    // Blue selection overlay
    const selectionOverlay = document.createElement('div');
    Object.assign(selectionOverlay.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      border: `2px solid ${token('color.border.selected', '#0C66E4')}`,
      borderRadius: token('radius.small', '3px'),
      background: `color-mix(in srgb, ${token(
        'color.background.accent.blue.subtlest',
        '#E9F2FF'
      )} 25%, transparent)`,
      pointerEvents: 'none',
      boxSizing: 'border-box'
    });
    dragImageRef.current.append(selectionOverlay);

    table?.parentElement?.append(dragImageRef.current);

    e.dataTransfer.setDragImage(
      dragImageRef.current,
      selectedThsIndexOffsetWidth - (e.target as HTMLElement)?.clientWidth / 2,
      SELECT_BUTTON_HEIGHT / 2
    );
  }
};
